import { Drawer, Pagination } from '@/components/commons';
import { AgentService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import { AgentEngineId, IAgentMessage } from '@/types/response';
import { Card, Form, InputNumber, Select, Space, Table, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from 'react-query';
import {
  ENGINE_TAG_COLOR,
  GenerationLogView,
  MESSAGE_STATUS_TAG,
} from '../agentShared';

const { Title, Paragraph, Text } = Typography;

interface IProps {
  title?: string;
}

type Filters = {
  page: number;
  limit: number;
  familyId?: number;
  engineId?: AgentEngineId;
};

export const AgentMessageList: React.FC<IProps> = ({ title }) => {
  const [filters, setFilters] = useState<Filters>({ page: 1, limit: 20 });
  // 家族IDは入力途中の中間値でクエリが走らないよう 500ms デバウンス
  const [familyIdInput, setFamilyIdInput] = useState<number | null>(null);
  const [detailMessage, setDetailMessage] = useState<IAgentMessage | null>(
    null,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        familyId: familyIdInput ?? undefined,
      }));
    }, 500);
    return () => clearTimeout(timer);
  }, [familyIdInput]);

  const { data: enginesData } = useQuery(
    [ENDPOINT.AGENT.LIST_ENGINES],
    () => AgentService.listEngines(),
    { staleTime: Infinity },
  );
  const engines = enginesData?.data ?? [];
  const engineLabel = (id: AgentEngineId) =>
    engines.find((e) => e.id === id)?.label ?? id;

  const { data, isLoading } = useQuery(
    [ENDPOINT.AGENT.LIST_MESSAGES, filters],
    () => AgentService.listMessages(filters),
    { keepPreviousData: true },
  );
  const messages = data?.data.items ?? [];
  const total = data?.data.total ?? 0;

  // 生成ログは一覧に含まれないため Drawer を開いたときに個別取得
  const { data: logData, isLoading: logLoading } = useQuery(
    [ENDPOINT.AGENT.GET_MESSAGE_GENERATION_LOG, detailMessage?.id],
    () => AgentService.getMessageGenerationLog(detailMessage!.id),
    { enabled: !!detailMessage },
  );

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters((prev) => ({ ...prev, page, limit: pageSize }));
  };

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header>
          <Title level={3} style={{ marginTop: 0 }}>
            AIエージェント 生成一覧
          </Title>
          <Paragraph type='secondary' style={{ marginBottom: 16 }}>
            エンジンが生成したメッセージの一覧です。行をクリックすると本文全文と
            生成ログ（検索クエリ・参照URL・選定ログ）を確認できます。
          </Paragraph>
        </header>

        <Card>
          <Form layout='inline'>
            <Form.Item label='家族ID'>
              <InputNumber
                style={{ width: 120 }}
                min={1}
                placeholder='全件'
                value={familyIdInput}
                onChange={(v) => setFamilyIdInput(v ?? null)}
              />
            </Form.Item>
            <Form.Item label='エンジン'>
              <Select
                style={{ width: 200 }}
                allowClear
                placeholder='全エンジン'
                value={filters.engineId}
                onChange={(v) =>
                  setFilters((prev) => ({ ...prev, page: 1, engineId: v }))
                }
                options={engines.map((e) => ({
                  value: e.id,
                  label: `${e.label}（${e.id}）`,
                }))}
              />
            </Form.Item>
          </Form>
        </Card>

        <Card>
          <Table
            rowKey='id'
            size='small'
            loading={isLoading}
            dataSource={messages}
            pagination={false}
            onRow={(record) => ({
              onClick: () => setDetailMessage(record),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: 'ID', dataIndex: 'id', width: 70 },
              { title: '家族', dataIndex: 'familyId', width: 70 },
              {
                title: 'エンジン',
                dataIndex: 'engineId',
                width: 140,
                render: (v: AgentEngineId) => (
                  <Tag color={ENGINE_TAG_COLOR[v]}>{engineLabel(v)}</Tag>
                ),
              },
              { title: '対象日', dataIndex: 'publishDate', width: 110 },
              {
                title: '状態',
                dataIndex: 'status',
                width: 100,
                render: (v: string) => {
                  const s = MESSAGE_STATUS_TAG[v] ?? {
                    label: v,
                    color: 'default',
                  };
                  return <Tag color={s.color}>{s.label}</Tag>;
                },
              },
              {
                title: '対象profile',
                dataIndex: 'targetProfileId',
                width: 100,
                render: (v: number | null) => v ?? '-',
              },
              {
                title: '本文（冒頭）',
                dataIndex: 'text',
                ellipsis: true,
                render: (v: string) => v.slice(0, 80),
              },
              {
                title: '作成日時',
                dataIndex: 'createdAt',
                width: 160,
                render: (v: string) => v?.replace('T', ' ').slice(0, 16),
              },
            ]}
          />
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Pagination
              total={total}
              pageSize={filters.limit}
              current={filters.page}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
            />
          </div>
        </Card>
      </div>

      {/* 詳細 Drawer */}
      <Drawer
        title={
          detailMessage
            ? `${detailMessage.publishDate} / ${engineLabel(detailMessage.engineId)} / family=${detailMessage.familyId} / id=${detailMessage.id}`
            : ''
        }
        open={!!detailMessage}
        onCancel={() => setDetailMessage(null)}
        onlyCancel
        cancelText='閉じる'
        width={1000}
      >
        {detailMessage && (
          <Space direction='vertical' size={16} style={{ width: '100%' }}>
            <Space wrap>
              <Tag
                color={
                  MESSAGE_STATUS_TAG[detailMessage.status]?.color ?? 'default'
                }
              >
                {MESSAGE_STATUS_TAG[detailMessage.status]?.label ??
                  detailMessage.status}
              </Tag>
              {detailMessage.surveyRoomMessageId && (
                <Text type='secondary' style={{ fontSize: 12 }}>
                  トークmessage id={detailMessage.surveyRoomMessageId}
                </Text>
              )}
              {detailMessage.targetProfileId && (
                <Text type='secondary' style={{ fontSize: 12 }}>
                  対象profile id={detailMessage.targetProfileId}
                </Text>
              )}
            </Space>
            <pre
              style={{
                background: '#f6ffed',
                padding: 16,
                borderRadius: 4,
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                margin: 0,
              }}
            >
              {detailMessage.text}
            </pre>
            {detailMessage.usage && (
              <Text type='secondary' style={{ fontSize: 12 }}>
                {detailMessage.usage.model} / {detailMessage.usage.provider} /
                in: {detailMessage.usage.input_tokens} / out:{' '}
                {detailMessage.usage.output_tokens}
              </Text>
            )}
            {logLoading ? (
              <Text type='secondary'>生成ログを読み込み中…</Text>
            ) : (
              <GenerationLogView log={logData?.data.generationLog ?? null} />
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
};
